import axios from 'axios';
import i18n from 'i18n';
import store from 'store';
import { setApiError } from 'store/apiError/actions';

// apply base url for axios
const API_URL =
  process.env.REACT_APP_API_URL || 'https://api.test.visasupport.de';

const axiosApi = axios.create({ baseURL: API_URL });

axiosApi.interceptors.request.use(config => {
  config.headers['Accept-Language'] = i18n.language;
  return config;
});

/**
 * Normalize both possible shapes and dispatch to the global apiError slice.
 *
 * Case 1 (successful HTTP, but backend says { success: false, status }):
 *   body = { success: false, status: 404, message, data }
 *
 * Case 2 (axios rejected with real HTTP 404/403):
 *   body = err.response = { status: 404, data: { detail } }
 */
const handleApiResponse = body => {
  if (!body) return body;

  // Case 1: backend envelope (has `success` field)
  if (typeof body.success === 'boolean') {
    if (body.success === false) {
      if (body.status === 404 || body.status === 403) {
        store.dispatch(
          setApiError({
            status: body.status,
            message: body.message || body.data?.detail,
          }),
        );
      }
      return body;
    }
    // success === true
    // const current = store.getState().apiError;
    // if (current) store.dispatch(clearApiError());
    return body;
  }

  // Case 2: raw axios error response (no `success`, has `status` + `data`)
  if (body.status && body.data && typeof body.data === 'object') {
    const status = body.status;
    const message = body.data.detail || body.data.message;
    if (status === 404 || status === 403) {
      store.dispatch(setApiError({ status, message }));
    }
    return body.data;
  }

  return body;
};

/**
 * Wrapper used by every verb — applies axios call, normalizes response,
 * and never throws (returns the normalized body instead).
 */
const request = async config => {
  try {
    const response = await axiosApi.request(config);
    return handleApiResponse(response.data);
  } catch (err) {
    // Real HTTP error → axios throws
    if (err.response) {
      return handleApiResponse(err.response);
    }
    // Network error / timeout — no response body; let caller toast it
    throw err;
  }
};

export async function get(url, config = {}) {
  return request({ method: 'get', url, ...config });
}

export async function post(url, data, config = {}) {
  return request({ method: 'post', url, data, ...config });
}

export async function put(url, data, config = {}) {
  return request({ method: 'put', url, data, ...config });
}

export async function del(url, config = {}) {
  return request({ method: 'delete', url, ...config });
}

export async function upload(url, data, config = {}) {
  return request({ method: 'post', url, data, ...config });
}

export async function updateFile(url, data, config = {}) {
  return request({ method: 'put', url, data, ...config });
}

export const fakeSleep = () =>
  new Promise(resolve => {
    setTimeout(() => {
      resolve(1);
    }, 300);
  });
